import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'cosmic_chat_history';
const MAX_HISTORY = 40;
const ASSISTANT_NAME = 'AI Chat';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    "Hello! I'm Cosmic Learning AI, your English tutor. Ask me about grammar, vocabulary, or how to practice reading, writing, listening, and speaking. How can I help you today?",
};

// Ephemeral: history lives in this browser tab only (sessionStorage), never on the server.
function loadHistory() {
  if (typeof window === 'undefined') return [WELCOME_MESSAGE];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch {
    // Ignore corrupt storage and start fresh.
  }
  return [WELCOME_MESSAGE];
}

function isSmallScreen() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const resizeComposer = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  };

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch {
      // Storage full or unavailable; keep working with in-memory state.
    }
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    resizeComposer();
  }, [open]);

  useEffect(() => {
    resizeComposer();
  }, [input]);

  // Size the mobile overlay to the visible viewport (covers keyboard + browser chrome).
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const vv = window.visualViewport;
    const updateViewportBox = () => {
      if (!isSmallScreen()) {
        root.style.removeProperty('--chat-vv-top');
        root.style.removeProperty('--chat-vv-height');
        root.classList.remove('chat-fullscreen-open');
        return;
      }
      root.classList.add('chat-fullscreen-open');
      if (!vv) {
        root.style.setProperty('--chat-vv-top', '0px');
        root.style.setProperty('--chat-vv-height', `${window.innerHeight}px`);
        return;
      }
      root.style.setProperty('--chat-vv-top', `${vv.offsetTop}px`);
      root.style.setProperty('--chat-vv-height', `${vv.height}px`);
    };
    updateViewportBox();
    vv?.addEventListener('resize', updateViewportBox);
    vv?.addEventListener('scroll', updateViewportBox);
    window.addEventListener('resize', updateViewportBox);
    return () => {
      vv?.removeEventListener('resize', updateViewportBox);
      vv?.removeEventListener('scroll', updateViewportBox);
      window.removeEventListener('resize', updateViewportBox);
      root.style.removeProperty('--chat-vv-top');
      root.style.removeProperty('--chat-vv-height');
      root.classList.remove('chat-fullscreen-open');
    };
  }, [open]);

  // Lock background scroll while the mobile sheet is open.
  useEffect(() => {
    if (!open || !isSmallScreen()) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [open]);

  // Close when tapping/clicking outside on desktop, or pressing Escape.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (isSmallScreen()) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Only available to signed-in users.
  if (!user) return null;

  const handleClear = () => {
    setMessages([WELCOME_MESSAGE]);
    setError('');
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setError('');
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    // Send prior turns (exclude the seeded welcome message) as context.
    const history = messages
      .filter((m) => m !== WELCOME_MESSAGE)
      .slice(-MAX_HISTORY)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);

    try {
      const res = await api.sendChatMessage({ messages: history, user_text: text });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div ref={containerRef} className={open ? 'chat-widget--open' : undefined}>
      {open && (
        <button
          type="button"
          className="chat-widget-backdrop"
          aria-label="Close AI tutor"
          onClick={() => setOpen(false)}
        />
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close AI tutor' : 'Open AI tutor'}
        aria-expanded={open}
        className={`chat-widget-launcher group ${open ? 'scale-95' : ''}`}
      >
        <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100" />
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3l1.6 4.9L18.5 9.5 13.6 11 12 16l-1.6-5L5.5 9.5l4.9-1.6L12 3z" />
            <circle cx="18.5" cy="18.5" r="1.5" />
          </svg>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ASSISTANT_NAME}
          className="chat-widget-panel"
        >
          <div className="chat-widget-header flex items-center justify-between gap-2 border-b border-border/50 px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
              </span>
              <p className="truncate bg-gradient-to-r from-primary to-accent bg-clip-text text-sm font-semibold tracking-tight text-transparent">
                {ASSISTANT_NAME}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md px-2.5 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close AI tutor"
                className="chat-widget-close flex items-center justify-center rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="chat-widget-messages space-y-3 px-3 py-3 sm:px-4 sm:py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[88%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-[0.9375rem] leading-relaxed sm:max-w-[85%] sm:text-sm ${
                    m.role === 'user'
                      ? 'rounded-br-sm bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-sm shadow-primary/20'
                      : 'rounded-bl-sm border border-border/50 bg-muted/50 text-foreground'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-border/50 bg-muted/50 px-4 py-3">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary" />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}
          </div>

          <div className="chat-widget-composer border-t border-border/50 px-3 py-2.5 sm:px-3 sm:py-3">
            <div className="flex items-end gap-2 rounded-2xl border border-border/60 bg-input/60 px-2 py-1.5 transition-colors focus-within:border-primary/50">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about English…"
                enterKeyHint="send"
                className="chat-widget-input min-h-10 flex-1 resize-none bg-transparent px-2 py-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none sm:min-h-0 sm:text-sm"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="chat-widget-send flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground transition-all hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40 sm:h-9 sm:w-9"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
